import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { INTERIOR_STYLES } from "../data/content";
import { Reveal, Overline } from "./Reveal";
import { motion } from "framer-motion";

export const InteriorStyles = () => (
  <section id="interiors" data-testid="interior-styles-section" className="blueprint-grid-dark bg-navy py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Overline light>Interior styles & cost variation</Overline>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
          Pick a style. See what it costs — and why.
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-blue-200/80">
          Turnkey interior rates per square foot, plus taxes and site variables.
          Design-only consultation starts at ₹25,000, up to 10% of project cost + visitation cost.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <Tabs defaultValue="minimal" className="mt-12" data-testid="interior-styles-tabs">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-0 rounded-none border border-white/20 bg-transparent p-0">
            {INTERIOR_STYLES.map((s) => (
              <TabsTrigger
                key={s.id}
                value={s.id}
                data-testid={`interior-tab-${s.id}`}
                className="rounded-none border-r border-white/20 px-5 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-blue-200/70 transition-colors data-[state=active]:bg-electric data-[state=active]:text-white hover:text-white"
              >
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {INTERIOR_STYLES.map((s) => (
            <TabsContent key={s.id} value={s.id} className="mt-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 gap-8 lg:grid-cols-12"
              >
                <div className="lg:col-span-7">
                  <div className="overflow-hidden border border-white/25" style={{ boxShadow: "6px 6px 0 0 #0055FF" }}>
                    <img
                      src={s.image}
                      alt={`${s.label} interior style by Subhankara`}
                      data-testid={`interior-image-${s.id}`}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="flex flex-col lg:col-span-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300">Turnkey Rate Band</p>
                  <p className="mt-2 font-mono text-2xl font-bold text-white sm:text-3xl" data-testid={`interior-rate-${s.id}`}>
                    {s.range}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-blue-300/80">+ taxes ± variables</p>
                  <p className="mt-5 text-sm leading-relaxed text-blue-100/85">{s.description}</p>
                  <ul className="mt-6 space-y-2.5 border-t border-white/15 pt-6">
                    {s.includes.map((inc) => (
                      <li key={inc} className="flex items-center gap-3 text-xs font-semibold text-blue-100">
                        <span className="h-1.5 w-1.5 rotate-45 bg-electric" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </Reveal>

      <Reveal delay={0.2}>
        <p className="mt-10 border-l-2 border-electric pl-4 font-mono text-xs leading-relaxed text-blue-200/70" data-testid="interior-rate-justification">
          Why the band? Material grade, custom vs. modular furniture, site conditions and scope of
          services move the final rate within the range. Your BOQ locks the number before work begins.
        </p>
      </Reveal>
    </div>
  </section>
);
